/**
 * ============================================================
 * CENTRALIZED WEBSOCKET MANAGER
 * ============================================================
 * 
 * Production-grade WebSocket management with:
 * - Single connection lifecycle management
 * - Exponential backoff reconnection with jitter
 * - Heartbeat/dead connection detection
 * - Automatic polling fallback
 * - Connection state machine
 * - Subscriber management
 * - Console noise suppression
 * 
 * This is the SINGLE SOURCE OF TRUTH for all realtime connections.
 * All components should use useWebSocketStatus() hook or subscribe
 * through this manager rather than creating independent connections.
 */

// ============================================================
// CONNECTION STATES
// ============================================================
export const WS_STATES = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  OFFLINE: 'OFFLINE',
  FALLBACK_POLLING: 'FALLBACK_POLLING'
};

// ============================================================
// CONFIGURATION
// ============================================================
const CONFIG = {
  // Reconnect settings
  RECONNECT_DELAYS: [1000, 2000, 5000, 10000, 20000, 30000], // Exponential backoff steps
  MAX_RECONNECT_ATTEMPTS: 10,
  JITTER_MAX: 500, // Max random jitter in ms
  
  // Heartbeat settings
  HEARTBEAT_INTERVAL: 25000, // Send ping every 25s
  HEARTBEAT_TIMEOUT: 35000, // Consider dead if no pong in 35s
  
  // Polling fallback settings
  POLLING_INTERVAL: 20000, // Poll every 20s when in fallback mode
  FALLBACK_THRESHOLD: 5, // Switch to polling after this many failed reconnects
  WS_RESTORE_CHECK_INTERVAL: 60000, // Check if WS is available again every 60s
  
  // Logging
  DEBUG_MODE: false, // Set to true for verbose logging
};

// ============================================================
// WEBSOCKET MANAGER CLASS
// ============================================================
class WebSocketManager {
  constructor() {
    // State
    this.state = WS_STATES.DISCONNECTED;
    this.ws = null;
    this.channels = new Map(); // channel -> Set of subscribers
    this.reconnectAttempts = 0;
    this.lastPongTime = null;
    this.isManualDisconnect = false;
    
    // Timers
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.heartbeatTimeoutTimer = null;
    this.pollingTimer = null;
    this.wsRestoreTimer = null;
    
    // Subscribers for state changes
    this.stateSubscribers = new Set();
    
    // Connection info
    this.baseUrl = null;
    this.token = null;
    this.userId = null;
    
    // Metrics
    this.metrics = {
      connectCount: 0,
      disconnectCount: 0,
      reconnectCount: 0,
      messageCount: 0,
      pollingFallbackCount: 0,
      lastConnectTime: null,
      lastDisconnectTime: null,
      sessionUptime: 0
    };
    
    // Bind methods
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
  }
  
  // --------------------------------------------------------
  // STATE MANAGEMENT
  // --------------------------------------------------------
  
  setState(newState) {
    if (this.state === newState) return;
    
    const oldState = this.state;
    this.state = newState;
    
    this.debug(`State transition: ${oldState} -> ${newState}`);
    
    // Notify subscribers
    this.stateSubscribers.forEach(callback => {
      try {
        callback(newState, oldState);
      } catch (err) {
        this.warn('State subscriber error:', err);
      }
    });
  }
  
  subscribeToState(callback) {
    this.stateSubscribers.add(callback);
    // Immediately notify of current state
    callback(this.state, null);
    return () => this.stateSubscribers.delete(callback);
  }
  
  // --------------------------------------------------------
  // CONNECTION LIFECYCLE
  // --------------------------------------------------------
  
  initialize(options = {}) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    this.baseUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    this.token = options.token || localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    this.userId = options.userId || 'anonymous';
    this.channel = options.channel || 'wyldecrest_helpdesk';
    
    if (this.token) {
      this.connect();
    }
  }
  
  connect(channel = null) {
    if (this.isManualDisconnect) return;
    if (this.state === WS_STATES.CONNECTED || this.state === WS_STATES.CONNECTING) return;
    
    // Use provided channel or default
    const wsChannel = channel || this.channel || 'wyldecrest_helpdesk';
    
    this.clearTimers();
    this.setState(this.reconnectAttempts > 0 ? WS_STATES.RECONNECTING : WS_STATES.CONNECTING);
    
    try {
      // Use the /api/realtime/ws endpoint which is properly routed through ingress
      const url = `${this.baseUrl}/api/realtime/ws/${wsChannel}?user_id=${encodeURIComponent(this.userId)}`;
      this.ws = new WebSocket(url);
      
      this.ws.onopen = this.handleOpen;
      this.ws.onclose = this.handleClose;
      this.ws.onerror = this.handleError;
      this.ws.onmessage = this.handleMessage;
      
    } catch (err) {
      this.warn('Failed to create WebSocket:', err.message);
      this.scheduleReconnect();
    }
  }
  
  disconnect() {
    this.isManualDisconnect = true;
    this.clearTimers();
    
    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on manual close
      this.ws.close();
      this.ws = null;
    }
    
    this.setState(WS_STATES.DISCONNECTED);
    this.metrics.disconnectCount++;
    this.metrics.lastDisconnectTime = Date.now();
  }
  
  // --------------------------------------------------------
  // EVENT HANDLERS
  // --------------------------------------------------------
  
  handleOpen() {
    this.debug('WebSocket connected');
    this.setState(WS_STATES.CONNECTED);
    this.reconnectAttempts = 0;
    this.lastPongTime = Date.now();
    this.metrics.connectCount++;
    this.metrics.lastConnectTime = Date.now();
    
    // Start heartbeat
    this.startHeartbeat();
    
    // Stop polling fallback if active
    this.stopPollingFallback();
    
    // Stop WS restore checker
    this.stopWsRestoreCheck();
  }
  
  handleClose(event) {
    this.debug(`WebSocket closed: code=${event.code}, reason=${event.reason}`);
    this.ws = null;
    this.stopHeartbeat();
    
    if (this.isManualDisconnect) return;
    
    this.metrics.disconnectCount++;
    this.metrics.lastDisconnectTime = Date.now();
    
    // Check if auth failure
    if (event.code === 4001 || event.code === 4002) {
      this.setState(WS_STATES.OFFLINE);
      this.warn('WebSocket auth failure, not reconnecting');
      return;
    }
    
    this.scheduleReconnect();
  }
  
  handleError(error) {
    // Suppress verbose error logging - only log in debug mode
    this.debug('WebSocket error occurred');
  }
  
  handleMessage(event) {
    this.metrics.messageCount++;
    
    // Handle pong for heartbeat
    if (event.data === 'pong') {
      this.lastPongTime = Date.now();
      return;
    }
    
    try {
      const data = JSON.parse(event.data);
      
      // Handle heartbeat/connected messages silently
      if (data.type === 'connected' || data.type === 'heartbeat' || data.type === 'pong') {
        this.lastPongTime = Date.now();
        return;
      }
      
      // Dispatch to channel subscribers
      this.dispatchToSubscribers(data);
      
    } catch (err) {
      // Silently ignore parse errors
    }
  }
  
  // --------------------------------------------------------
  // RECONNECTION WITH EXPONENTIAL BACKOFF + JITTER
  // --------------------------------------------------------
  
  scheduleReconnect() {
    if (this.isManualDisconnect) return;
    
    this.reconnectAttempts++;
    this.metrics.reconnectCount++;
    
    // Check if we should switch to polling fallback
    if (this.reconnectAttempts >= CONFIG.FALLBACK_THRESHOLD) {
      this.startPollingFallback();
      return;
    }
    
    this.setState(WS_STATES.RECONNECTING);
    
    // Calculate delay with jitter
    const delayIndex = Math.min(this.reconnectAttempts - 1, CONFIG.RECONNECT_DELAYS.length - 1);
    const baseDelay = CONFIG.RECONNECT_DELAYS[delayIndex];
    const jitter = Math.random() * CONFIG.JITTER_MAX;
    const delay = baseDelay + jitter;
    
    this.debug(`Scheduling reconnect in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }
  
  // --------------------------------------------------------
  // HEARTBEAT / DEAD CONNECTION DETECTION
  // --------------------------------------------------------
  
  startHeartbeat() {
    this.stopHeartbeat();
    
    // Send ping periodically
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
        
        // Set timeout to check for pong
        this.heartbeatTimeoutTimer = setTimeout(() => {
          this.checkHeartbeatTimeout();
        }, CONFIG.HEARTBEAT_TIMEOUT - CONFIG.HEARTBEAT_INTERVAL);
      }
    }, CONFIG.HEARTBEAT_INTERVAL);
  }
  
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }
  
  checkHeartbeatTimeout() {
    const timeSinceLastPong = Date.now() - (this.lastPongTime || 0);
    
    if (timeSinceLastPong > CONFIG.HEARTBEAT_TIMEOUT) {
      this.warn('Heartbeat timeout - connection appears dead, reconnecting');
      
      // Force close and reconnect
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close();
        this.ws = null;
      }
      
      this.scheduleReconnect();
    }
  }
  
  // --------------------------------------------------------
  // POLLING FALLBACK
  // --------------------------------------------------------
  
  startPollingFallback() {
    this.debug('Switching to polling fallback mode');
    this.setState(WS_STATES.FALLBACK_POLLING);
    this.metrics.pollingFallbackCount++;
    
    this.stopPollingFallback();
    
    // Start polling
    this.pollingTimer = setInterval(() => {
      this.pollForUpdates();
    }, CONFIG.POLLING_INTERVAL);
    
    // Immediately poll once
    this.pollForUpdates();
    
    // Start background WS restore checker
    this.startWsRestoreCheck();
  }
  
  stopPollingFallback() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
  
  async pollForUpdates() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl || !this.token) return;
    
    try {
      // Poll a lightweight status endpoint
      const response = await fetch(`${backendUrl}/api/phs/realtime-poll`, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.events?.length > 0) {
          data.events.forEach(event => this.dispatchToSubscribers(event));
        }
      }
    } catch (err) {
      // Silent polling failure
    }
  }
  
  // --------------------------------------------------------
  // WS RESTORE CHECK (BACKGROUND)
  // --------------------------------------------------------
  
  startWsRestoreCheck() {
    this.stopWsRestoreCheck();
    
    this.wsRestoreTimer = setInterval(() => {
      this.attemptWsRestore();
    }, CONFIG.WS_RESTORE_CHECK_INTERVAL);
  }
  
  stopWsRestoreCheck() {
    if (this.wsRestoreTimer) {
      clearInterval(this.wsRestoreTimer);
      this.wsRestoreTimer = null;
    }
  }
  
  attemptWsRestore() {
    if (this.state !== WS_STATES.FALLBACK_POLLING) return;
    
    this.debug('Attempting to restore WebSocket connection');
    this.reconnectAttempts = 0; // Reset attempts
    this.connect();
  }
  
  // --------------------------------------------------------
  // CHANNEL SUBSCRIPTION
  // --------------------------------------------------------
  
  subscribe(channel, callback) {
    if (!this.channels.has(channel)) {
      this.channels.set(channel, new Set());
    }
    this.channels.get(channel).add(callback);
    
    return () => {
      const subs = this.channels.get(channel);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          this.channels.delete(channel);
        }
      }
    };
  }
  
  dispatchToSubscribers(data) {
    // Dispatch to all channels (broadcast)
    this.channels.forEach((subscribers, channel) => {
      subscribers.forEach(callback => {
        try {
          callback(data, channel);
        } catch (err) {
          this.warn('Subscriber callback error:', err);
        }
      });
    });
  }
  
  // --------------------------------------------------------
  // SEND MESSAGE
  // --------------------------------------------------------
  
  send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(typeof message === 'string' ? message : JSON.stringify(message));
      return true;
    }
    return false;
  }
  
  // --------------------------------------------------------
  // UTILITY METHODS
  // --------------------------------------------------------
  
  clearTimers() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    this.stopPollingFallback();
    this.stopWsRestoreCheck();
  }
  
  getMetrics() {
    return {
      ...this.metrics,
      currentState: this.state,
      reconnectAttempts: this.reconnectAttempts,
      channelCount: this.channels.size,
      subscriberCount: Array.from(this.channels.values()).reduce((sum, set) => sum + set.size, 0),
      sessionUptime: this.metrics.lastConnectTime 
        ? Date.now() - this.metrics.lastConnectTime 
        : 0
    };
  }
  
  isConnected() {
    return this.state === WS_STATES.CONNECTED;
  }
  
  isAvailable() {
    return this.state === WS_STATES.CONNECTED || this.state === WS_STATES.FALLBACK_POLLING;
  }
  
  // --------------------------------------------------------
  // LOGGING (THROTTLED)
  // --------------------------------------------------------
  
  debug(...args) {
    if (CONFIG.DEBUG_MODE) {
      console.debug('[WebSocketManager]', ...args);
    }
  }
  
  warn(...args) {
    // Throttle warnings to prevent spam
    if (!this._lastWarnTime || Date.now() - this._lastWarnTime > 5000) {
      console.warn('[WebSocketManager]', ...args);
      this._lastWarnTime = Date.now();
    }
  }
  
  // --------------------------------------------------------
  // RESET (FOR TOKEN CHANGE)
  // --------------------------------------------------------
  
  reset(newToken) {
    this.disconnect();
    this.isManualDisconnect = false;
    this.reconnectAttempts = 0;
    this.token = newToken;
    
    if (newToken) {
      this.connect();
    }
  }
}

// ============================================================
// SINGLETON INSTANCE
// ============================================================
const wsManager = new WebSocketManager();

// ============================================================
// EXPORTS
// ============================================================
export default wsManager;
export { WebSocketManager, CONFIG };
